import Button, { StyledButton } from "@components/buttons/Button"
import ColorSwatches, {
  ActiveIndicator,
  ColorSwatch,
  ImageSwatch,
} from "@components/ColorSwatches"
import { Chevron } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16MainStyles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import { getProductImageAlt } from "@utils/altText"
import { getIn } from "@utils/index"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import { BreadcrumbButton, Headers } from "./common"

const openProps = `
  transform: translateX(0);
  transition: transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  transform: translateX(100%);
  transition: transform ${animation.medium} ${cubicBezier};
  will-change: transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 3;
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}
`

const overlayActiveProps = `
  opacity: 0.1
  transition: opacity ${animation.medium} ${cubicBezier};
`
const Overlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  will-change: opacity;
  opacity: 0;
  transition: opacity ${animation.kindaLong} ${cubicBezier};
  background-color: ${colors.black};
  pointer-events: none;
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 3;
  ${({ $isVisible }) => ($isVisible ? overlayActiveProps : "")}
`

const FormContainer = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  padding: 26px 20px;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    padding: 0 calc(0.5 / 12 * 100vw);
  }
`

const FormContent = styled.div``

const FormRow = styled.div`
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  display: flex;
  margin-bottom: 16px;

  @media ${breakpoints.laptop} {
    margin-bottom: 0;
  }
`

export const ImageWrapper = styled.div<{ $hoverType?: string }>`
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  width: 120px;
  height: 120px;
  background-color: ${colors.backgroundGray};

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }

  @media ${breakpoints.laptop} {
    width: calc(1.4 / 12 * 100vw);
    height: calc(1.4 / 12 * 100vw);
    margin-right: 32px;
  }
`

const Content = styled.div`
  width: 100%;
  margin-left: 12px;

  @media ${breakpoints.laptop} {
    width: auto;
    margin-left: 0;
  }
`

const Row = styled.div`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  display: flex;
`

const ProductName = styled.div`
  ${h5Styles}
  margin-bottom: 10px;

  @media ${breakpoints.laptop} {
    margin-bottom: 16px;
  }
`

const Price = styled.div`
  ${smallcapsBold13Styles}
  margin-bottom: 2px;
  color: ${colors.black};

  @media ${breakpoints.laptop} {
    font-size: 16px;
  }
`

const DiscountedPrice = styled.div`
  ${smallcapsBold13Styles}
  margin-right: 8px;
  text-decoration: line-through;
  color: ${colors.mediumGray};

  @media ${breakpoints.laptop} {
    font-size: 16px;
  }
`

const buttonProps = `
  display: flex;
  width: 100%;
  height: 56px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.dividerLineGray};
  cursor: pointer;
`

const buttonTextProps = `
  ${body16MainStyles}
  opacity: 0.9;
`

const buttonLightTextProps = `
  ${body16MainStyles}
  color: ${colors.mediumGray};
`

const ChevronIcon = styled.div`
  height: 32px;
  width: 32px;
  transform: rotate(90deg);

  @media ${breakpoints.tablet} {
    margin-right: 6px;
  }
`

const LanguageSizeButton = styled.a<{ $canClick: boolean }>`
  ${buttonProps}
  ${({ $canClick }) => (!$canClick ? "cursor: initial" : "")}
`

const LanguageSizeTextWrapper = styled.div`
  display: flex;
`

const LanguageSizeText = styled.div`
  ${buttonTextProps}
  margin-right: 8px;
`

const LanguageSizeLightText = styled.div`
  ${buttonLightTextProps}
`

const ColorWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: solid 1px ${colors.dividerLineGray};
`

const ColorTextWrapper = styled.div`
  display: flex;
  padding: 16px 0;
  flex-shrink: 0;
  align-items: center;
`

const ColorText = styled.div`
  ${buttonTextProps}
  margin-right: 8px;
`

const ColorLightText = styled.div`
  ${buttonLightTextProps}
`

const SwatchWrapper = styled.div`
  transform: translateX(10px);

  @media ${breakpoints.laptop} {
    transform: none;
  }

  ${ColorSwatch} {
    margin-top: 16px;
  }

  ${ImageSwatch} {
    margin-top: 16px;
  }

  ${ActiveIndicator} {
    height: 2px;
    margin-right: 16px;
  }
`

const Swatches = styled(ColorSwatches)`
  width: auto;
`

const Footer = styled.div`
  display: flex;
  margin-top: 32px;

  ${StyledButton} {
    height: 56px;
  }
`

interface FormProps {
  availableVariants: Array<any>
  onClickLanguageSelector?: (productLanguages) => void
  onClickSizeSelector?: (productSizes) => void
  onRequestClose: () => void
  onRequestCloseLanguageSelector?: () => void
  onRequestCloseSizeSelector?: () => void
  productName: string
  variants: Array<{
    color?: string
    compareAtPrice?: string
    hexString?: string
    images: Array<string>
    language?: string
    name: string
    onAddToCart: any
    outOfStockMessage?: string
    price: string
    size?: string
    sku: string
    swatchImage?: string
  }>
}

const Form: React.FC<FormProps> = ({
  availableVariants,
  onClickLanguageSelector,
  onClickSizeSelector,
  onRequestClose,
  onRequestCloseLanguageSelector,
  onRequestCloseSizeSelector,
  productName,
  variants,
}) => {
  const [selectedVariant, setSelectedVariant] = React.useState(0)

  const handleAddToCart = (e) => {
    e.stopPropagation()
    variants[selectedVariant]?.onAddToCart(1)
  }

  const handleCloseForm = (e) => {
    e.stopPropagation()
    onRequestClose()
  }

  const openLanguageSelector = (productLanguages) => {
    if (!R.isEmpty(productLanguages) && onClickLanguageSelector) {
      onClickLanguageSelector(productLanguages)
    }
  }

  const [language, setLanguage] = React.useState(undefined)

  const getClickHandlerForLanguage = (language) => (e) => {
    e.stopPropagation()
    const currentColor = variants[selectedVariant]?.color
    let variantIndex =
      currentColor &&
      (R.findIndex(({ color, language: variantLanguage }) => {
        return color === currentColor && variantLanguage === language
      })(variants) as any)

    if (variantIndex < 0) {
      variantIndex = R.findIndex(({ language: variantLanguage }) => {
        return variantLanguage === language
      })(variants)
    }

    setLanguage(language)
    setSelectedVariant(variantIndex)

    if (onRequestCloseLanguageSelector) {
      onRequestCloseLanguageSelector()
    }
  }

  const productLanguages: any = R.pipe(
    R.reject(({ language }: any) => !language) as any,
    R.uniqBy(R.prop("language")) as any,
    R.map(({ language }: any) => ({
      language,
      onClick: getClickHandlerForLanguage(language),
    }))
  )(variants as any)

  const canClickLanguageButton = productLanguages?.length > 1

  const openSizeSelector = (productSizes) => {
    if (!R.isEmpty(productSizes) && onClickSizeSelector) {
      onClickSizeSelector(productSizes)
    }
  }

  const swatchColors = variants.map((variant, index) => ({
    ...R.pick(["name", "language", "size", "hexString", "swatchImage"])(
      variant
    ),
    onClick: () => setSelectedVariant(index),
  }))

  const [size, setSize] = React.useState(undefined)

  const getClickHandlerForSize = (size) => (e) => {
    e.stopPropagation()
    const currentColor = variants[selectedVariant]?.color
    let variantIndex =
      currentColor &&
      (R.findIndex(({ color, size: variantSize }: any) => {
        return color === currentColor && variantSize === size
      })(variants) as any)

    if (variantIndex < 0) {
      variantIndex = R.findIndex(({ size: variantSize }) => {
        return variantSize === size
      })(variants)
    }

    setSize(size)
    setSelectedVariant(variantIndex)

    if (onRequestCloseSizeSelector) {
      onRequestCloseSizeSelector()
    }
  }

  const productSizes: any = R.pipe(
    R.reject(({ size }: any) => !size) as any,
    R.uniqBy(R.prop("size")) as any,
    R.map(({ size }: any) => ({
      onClick: getClickHandlerForSize(size),
      size,
    }))
  )(variants as any)

  const canClickSizeButton = productSizes?.length > 1

  React.useEffect(() => {
    setSize(getIn(productLanguages, "0.language", undefined))
    setSize(getIn(productSizes, "0.size", undefined))
  }, [])

  React.useEffect(() => {
    if (
      variants[selectedVariant]?.language &&
      variants[selectedVariant]?.language !== language
    ) {
      const updatedLanguage = R.find(
        ({ language }) => language === variants[selectedVariant]?.language
      )(productLanguages) as any

      if (updatedLanguage) {
        setLanguage(updatedLanguage.language)
      }
    }

    if (
      variants[selectedVariant]?.size &&
      variants[selectedVariant]?.size !== size
    ) {
      const updatedSize = R.find(
        ({ size }) => size === variants[selectedVariant]?.size
      )(productSizes) as any

      if (updatedSize) {
        setSize(updatedSize.size)
      }
    }
  }, [variants, selectedVariant])

  const omitLanguageSize: any = R.map(R.omit(["language", "size"]))

  const getColorsForLanguage = R.pipe(
    R.filter(
      ({ language: languageForColor }: any) => languageForColor === language
    ),
    omitLanguageSize
  )

  const getColorsForSize = R.pipe(
    R.filter(({ size: sizeForColor }: any) => sizeForColor === size),
    omitLanguageSize
  )

  const colorsToShow: any = language
    ? getColorsForLanguage(swatchColors)
    : size
      ? getColorsForSize(swatchColors)
      : omitLanguageSize(swatchColors)

  const isAvailable =
    availableVariants.indexOf(variants[selectedVariant]?.sku) !== -1

  return (
    <>
      <Overlay $isVisible={!!productName} />
      <Container $isOpen={!!productName}>
        <Headers>
          <BreadcrumbButton label="Back" onClick={handleCloseForm} />
        </Headers>
        <FormContainer>
          <DesktopOnly>
            <ImageWrapper>
              <ImageHelper
                alt={getProductImageAlt({
                  colorName: variants[selectedVariant]?.name,
                  imageType: "gallery",
                  productName: productName || "Product",
                })}
                src={variants[selectedVariant]?.images?.[0]}
              />
            </ImageWrapper>
          </DesktopOnly>
          <FormContent>
            <FormRow>
              <MobileAndTabletOnly>
                <ImageWrapper>
                  <ImageHelper
                    alt={getProductImageAlt({
                      colorName: variants[selectedVariant]?.name,
                      imageType: "gallery",
                      productName: productName || "Product",
                    })}
                    src={variants[selectedVariant]?.images?.[0]}
                  />
                </ImageWrapper>
              </MobileAndTabletOnly>
              <Content>
                <ProductName>{productName}</ProductName>
                <Row>
                  {variants[selectedVariant]?.compareAtPrice && (
                    <DiscountedPrice>
                      {`$${variants[selectedVariant]?.compareAtPrice}`}
                    </DiscountedPrice>
                  )}
                  <Price>{`$${variants[selectedVariant]?.price}`}</Price>
                </Row>
              </Content>
            </FormRow>
            {language && (
              <LanguageSizeButton
                $canClick={canClickLanguageButton}
                onClick={(e) => {
                  if (canClickLanguageButton) {
                    e.stopPropagation()
                    openLanguageSelector(productLanguages)
                  }
                }}
              >
                <LanguageSizeTextWrapper>
                  <LanguageSizeText>Language:</LanguageSizeText>
                  <LanguageSizeLightText>{language}</LanguageSizeLightText>
                </LanguageSizeTextWrapper>
                {canClickLanguageButton && (
                  <ChevronIcon>
                    <Chevron strokeColor={colors.black} />
                  </ChevronIcon>
                )}
              </LanguageSizeButton>
            )}
            {size && (
              <LanguageSizeButton
                $canClick={canClickSizeButton}
                onClick={(e) => {
                  if (canClickSizeButton) {
                    e.stopPropagation()
                    openSizeSelector(productSizes)
                  }
                }}
              >
                <LanguageSizeTextWrapper>
                  <LanguageSizeText>Size:</LanguageSizeText>
                  <LanguageSizeLightText>{size}</LanguageSizeLightText>
                </LanguageSizeTextWrapper>
                {canClickSizeButton && (
                  <ChevronIcon>
                    <Chevron strokeColor={colors.black} />
                  </ChevronIcon>
                )}
              </LanguageSizeButton>
            )}
            <ColorWrapper>
              <ColorTextWrapper>
                <ColorText>Color:</ColorText>
                <ColorLightText>
                  {variants[selectedVariant]?.color}
                </ColorLightText>
              </ColorTextWrapper>
              <SwatchWrapper>
                <Swatches
                  activeSwatchName={variants[selectedVariant]?.name}
                  colors={colorsToShow}
                  size="24px"
                />
              </SwatchWrapper>
            </ColorWrapper>
            <Footer>
              <Button
                disabled={!isAvailable}
                onClick={handleAddToCart}
                text={
                  isAvailable
                    ? "Add to Cart"
                    : variants[selectedVariant]?.outOfStockMessage ||
                      "Out of Stock"
                }
              />
            </Footer>
          </FormContent>
        </FormContainer>
      </Container>
    </>
  )
}

export default Form
